import type { Dispatch, SetStateAction } from 'react';

import { StaticHeadersForm } from '@/components/form/StaticHeadersForm';
import {
  COMPLEX_LOGIN_FORM_INITIAL,
  type ComplexLoginFormData,
  CredentialFormBody,
  CredentialType,
  CredentialTypeSelection,
  IdentitySetManager,
  validateComplexLoginMulti,
} from '@/sections/asset/components/tabs/credentials/credentialForms';
import {
  InputsStep as RecipeInputsStep,
  OverviewStep as RecipeOverviewStep,
  RequestStep as RecipeRequestStep,
  SessionStep as RecipeSessionStep,
} from '@/sections/asset/components/tabs/credentials/RecipeWizard/steps';
import type { WizardCredentialData } from '@/sections/seeds/components/modals/AddSeedByTypeModal/AddSeedByTypeModal.types';

import { collectFieldErrors } from './credentialStep.util';

// Stable no-op setter for CredentialFormBody's required (but unused for
// non-COMPLEX types) complexLoginForm prop — COMPLEX_LOGIN renders
// IdentitySetManager, so this is never invoked (mirrors CredentialFormStep).
const noopSetComplexLoginForm: Dispatch<
  SetStateAction<ComplexLoginFormData>
> = () => {};

export type CredentialEditorBodyProps = {
  data: WizardCredentialData;
  onChange: (next: WizardCredentialData) => void;
  isWebService: boolean;
  webAppUrl: string;
  disabledTypes?: Partial<Record<CredentialType, string>>;
};

/**
 * Editor body for ONE credential bundle (a per-app Custom row or the Shared
 * config). Renders the type picker plus the chosen type's form, all controlled
 * into `data` via `onChange` — NO local secret useState (P2-2). The type picker
 * ALWAYS renders (the former `showTypePicker` prop was dead — no caller ever
 * passed false). RECIPE renders the recipe builder's entry step scoped to this
 * bundle's own draft. Mirrors CredentialFormStep's body, scoped to a passed-in
 * bundle instead of the wizard `configure` slice.
 *
 * Disabled-type guard: when `disabledTypes[data.selectedType]` is truthy, the
 * bundle's selected type is not available on this surface (e.g. RECIPE is
 * Custom-only on the shared surface — a stale bundle can still carry it). In
 * that case we render the type picker + the reason notice INSTEAD of the
 * per-type editor, so the user can switch to an available method. This is a
 * stale-data defense for the shared surface.
 */
export function CredentialEditorBody({
  data,
  onChange,
  isWebService,
  webAppUrl,
  disabledTypes,
}: CredentialEditorBodyProps) {
  const update = (patch: Partial<WizardCredentialData>) =>
    onChange({ ...data, ...patch });

  const makeSetter =
    <K extends keyof WizardCredentialData>(key: K) =>
    (action: SetStateAction<WizardCredentialData[K]>) =>
      update({
        [key]:
          typeof action === 'function'
            ? (
                action as (
                  prev: WizardCredentialData[K]
                ) => WizardCredentialData[K]
              )(data[key])
            : action,
      } as Partial<WizardCredentialData>);

  const typePicker = (
    <CredentialTypeSelection
      isWebService={isWebService}
      selectedType={data.selectedType}
      onTypeChange={type => update({ selectedType: type })}
      disabledTypes={disabledTypes}
    />
  );

  const disabledReason = disabledTypes?.[data.selectedType];
  if (disabledReason) {
    // The type picker (CredentialTypeSelection) already renders the reason on
    // the disabled card, so the guard notice only adds the recovery hint —
    // duplicating the reason string here would double it on the surface.
    return (
      <div className="flex flex-col gap-4">
        {typePicker}
        <p className="text-chariot-text-secondary text-sm" role="status">
          Choose an available method above.
        </p>
      </div>
    );
  }

  if (data.selectedType === CredentialType.COMPLEX_LOGIN) {
    const validation = validateComplexLoginMulti(data.complexLogin);
    return (
      <div className="flex flex-col gap-4">
        {typePicker}
        <IdentitySetManager
          value={data.complexLogin}
          onChange={next => update({ complexLogin: next })}
          webAppUrl={webAppUrl}
          validation={validation}
          attempted
        />
        <span className="text-chariot-text-secondary text-sm">
          {validation.readyCount} of {data.complexLogin.identities.length} ready
        </span>
      </div>
    );
  }

  if (data.selectedType === CredentialType.RECIPE) {
    // Option B / B1: a recipe is host-specific, authored per-app via Custom.
    // Mount the RecipeWizard's authoring steps inline (Overview→Inputs→Request
    // →Session), all driven off this bundle's own `recipeDraft` via a single
    // setter. The wizard's Review step is intentionally omitted — there is no
    // per-app "Save Recipe" here; the parent wizard's Add Seeds button commits,
    // and the row status pill (collectFieldErrors) signals readiness.
    const setRecipeDraft = makeSetter('recipeDraft');
    const recipeErrors = collectFieldErrors(data);
    return (
      <div className="flex flex-col gap-4">
        {typePicker}
        <RecipeOverviewStep
          data={data.recipeDraft}
          setData={setRecipeDraft}
          webAppUrl={webAppUrl}
        />
        <RecipeInputsStep data={data.recipeDraft} setData={setRecipeDraft} />
        <RecipeRequestStep data={data.recipeDraft} setData={setRecipeDraft} />
        <RecipeSessionStep data={data.recipeDraft} setData={setRecipeDraft} />
        {recipeErrors.length > 0 && (
          <ul className="space-y-1" aria-live="polite">
            {recipeErrors.map(message => (
              <li key={message} className="text-error text-xs">
                {message}
              </li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  const fieldErrors = collectFieldErrors(data);

  return (
    <div className="flex flex-col gap-4">
      {typePicker}
      <div className="border-chariot-stroke rounded-lg border p-4">
        {data.selectedType === CredentialType.STATIC_TOKEN ? (
          <StaticHeadersForm
            secret
            label={data.staticTokenForm.label}
            onLabelChange={label =>
              update({
                staticTokenForm: { ...data.staticTokenForm, label },
              })
            }
            headers={data.staticTokenForm.headers}
            onHeadersChange={headers =>
              update({
                staticTokenForm: { ...data.staticTokenForm, headers },
              })
            }
          />
        ) : (
          <CredentialFormBody
            selectedType={data.selectedType}
            basicForm={data.basicForm}
            setBasicForm={makeSetter('basicForm')}
            recordingForm={data.recordingForm}
            setRecordingForm={makeSetter('recordingForm')}
            bearerTokenForm={data.bearerTokenForm}
            setBearerTokenForm={makeSetter('bearerTokenForm')}
            apiKeyForm={data.apiKeyForm}
            setApiKeyForm={makeSetter('apiKeyForm')}
            complexLoginForm={COMPLEX_LOGIN_FORM_INITIAL}
            setComplexLoginForm={noopSetComplexLoginForm}
          />
        )}
      </div>

      {fieldErrors.length > 0 && (
        <ul className="space-y-1" aria-live="polite">
          {fieldErrors.map(message => (
            <li key={message} className="text-error text-xs">
              {message}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
